import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { AdminFilterProvider, useAdminFilter, PROFIT_DATE_FILTERS } from "@/context/AdminFilterContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LogOut, Save, Loader2, Plus, Trash2, ExternalLink, ArrowUp, ArrowDown, X, Search, ChevronDown } from "lucide-react";
import { Field, Input, Textarea, Select, ImageUploader, VideoUploader } from "@/components/admin/AdminControls";
import FinanceTrackerPanel from "@/components/admin/FinanceTrackerPanel";

function ImageListEditor({ value = [], onChange }) {  const arr = Array.isArray(value) ? value : [];
  const addOne = (url) => onChange([...arr, url]);
  const removeAt = (i) => onChange(arr.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {arr.map((src, i) => (
          <div key={i} className="relative border border-[#2A2A2A] aspect-square overflow-hidden group">
            <img src={src} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 w-7 h-7 bg-black/80 border border-red-500/40 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <ImageUploader label="Add gallery image" value="" onChange={addOne} />
    </div>
  );
}

const TABS = [
  { id: "settings", label: "Site & Brand" },
  { id: "orders", label: "Orders" },
  { id: "expenses", label: "Expenses" },
  { id: "other_income", label: "Other Income" },
  { id: "seo", label: "SEO" },
  { id: "hero", label: "Hero" },
  { id: "collections", label: "Collections" },
  { id: "pages", label: "Pages" },
  { id: "gallery", label: "Customer Gallery" },
  { id: "pillars", label: "Why Choose" },
  { id: "steps", label: "How It Works" },
  { id: "faqs", label: "FAQs" },
  { id: "tradefair", label: "Trade Fair" },
  { id: "sections", label: "Section Copy" },
];

function Saver({ status }) {
  if (!status) return null;
  return (
    <span className={`text-xs uppercase tracking-[0.22em] ${status === "saved" ? "text-emerald-400" : status === "saving" ? "text-[#D4AF37]" : "text-red-400"}`}>
      {status === "saving" ? "Saving…" : status === "saved" ? "Saved ✓" : "Error"}
    </span>
  );
}

function useSettingsForm() {
  const { refresh } = useSettings();
  const qc = useQueryClient();
  const { data: initial } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => api.get("/settings").then((r) => r.data),
    staleTime: 0,
  });
  const [overrides, setOverrides] = useState({});
  const [status, setStatus] = useState("");
  const s = initial ? { ...initial, ...overrides } : null;
  const set = (k, v) => setOverrides((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setStatus("saving");
    try {
      await api.put("/admin/settings", s);
      await refresh();
      // Ensure storefront reflects Global Sale / Free Shipping / etc. immediately.
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
      setStatus("saved");
      setTimeout(() => setStatus(""), 2000);
    } catch {
      setStatus("error");
    }
  };
  return { s, set, save, status };
}

function SaveBar({ save, status }) {
  return (
    <div className="flex items-center gap-4 sticky bottom-0 bg-[#0A0A0A] py-3 -mx-5 md:-mx-8 px-5 md:px-8 border-t border-[#1F1F1F]">
      <button onClick={save} className="btn-primary" data-testid="settings-save">
        <Save className="w-4 h-4" /> Save changes
      </button>
      <Saver status={status} />
    </div>
  );
}

function SettingsTab() {
  const { s, set, save, status } = useSettingsForm();
  if (!s) return <div className="text-white/40">Loading…</div>;
  return (
    <div className="space-y-8" data-testid="admin-settings-form">
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Brand</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Brand Name"><Input value={s.brand_name} onChange={(v) => set("brand_name", v)} testId="settings-brand-name" /></Field>
          <Field label="Tagline"><Input value={s.tagline} onChange={(v) => set("tagline", v)} testId="settings-tagline" /></Field>
        </div>
        <div className="mt-5">
          <ImageUploader label="Brand Logo" value={s.logo_image} onChange={(v) => set("logo_image", v)} testId="settings-logo-upload" />
        </div>
      </div>

      <div className="gold-hairline" />

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Contact</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="WhatsApp Number"><Input value={s.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} testId="settings-wa-number" placeholder="+44(0)7424986641" /></Field>
          <Field label="WhatsApp Default Message"><Input value={s.cta_message} onChange={(v) => set("cta_message", v)} testId="settings-cta-message" /></Field>
          <Field label="Email"><Input value={s.email} onChange={(v) => set("email", v)} type="email" placeholder="hello@example.com" /></Field>
          <Field label="Business Address"><Input value={s.business_address} onChange={(v) => set("business_address", v)} placeholder="City, Country" /></Field>
        </div>
      </div>

      <div className="gold-hairline" />

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Social Media</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Instagram URL"><Input value={s.instagram_url} onChange={(v) => set("instagram_url", v)} placeholder="https://instagram.com/..." /></Field>
          <Field label="TikTok URL"><Input value={s.tiktok_url} onChange={(v) => set("tiktok_url", v)} placeholder="https://tiktok.com/@..." /></Field>
          <Field label="Facebook URL"><Input value={s.facebook_url} onChange={(v) => set("facebook_url", v)} placeholder="https://facebook.com/..." /></Field>
        </div>
      </div>

      <div className="gold-hairline" />

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Trust Badges (Hero)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Stat #1 Value"><Input value={s.stat_customers} onChange={(v) => set("stat_customers", v)} /></Field>
          <Field label="Stat #1 Label"><Input value={s.stat_customers_label} onChange={(v) => set("stat_customers_label", v)} /></Field>
          <Field label="Stat #2 Value"><Input value={s.stat_repeat} onChange={(v) => set("stat_repeat", v)} /></Field>
          <Field label="Stat #2 Label"><Input value={s.stat_repeat_label} onChange={(v) => set("stat_repeat_label", v)} /></Field>
          <Field label="Stat #3 Label"><Input value={s.stat_premium_label} onChange={(v) => set("stat_premium_label", v)} /></Field>
          <Field label="Stat #4 Label"><Input value={s.stat_delivery_label} onChange={(v) => set("stat_delivery_label", v)} /></Field>
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Currency Rate Overrides</h3>
        <p className="text-white/55 text-sm -mt-3 mb-5 max-w-2xl">
          Leave at <span className="text-white/80">0</span> to use the live exchange rate. Set a value to pin a custom rate per £1 (useful for the NGN parallel market). Example: enter <span className="text-[#D4AF37]">2400</span> for NGN to charge ₦2,400 per £1.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="EUR per £1 (0 = live rate)">
            <Input type="number" value={s.eur_rate_override} onChange={(v) => set("eur_rate_override", Number(v) || 0)} testId="settings-eur-override" />
          </Field>
          <Field label="USD per £1 (0 = live rate)">
            <Input type="number" value={s.usd_rate_override} onChange={(v) => set("usd_rate_override", Number(v) || 0)} testId="settings-usd-override" />
          </Field>
          <Field label="CAD per £1 (0 = live rate)">
            <Input type="number" value={s.cad_rate_override} onChange={(v) => set("cad_rate_override", Number(v) || 0)} testId="settings-cad-override" />
          </Field>
          <Field label="NGN per £1 (0 = live rate)">
            <Input type="number" value={s.ngn_rate_override} onChange={(v) => set("ngn_rate_override", Number(v) || 0)} testId="settings-ngn-override" />
          </Field>
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Financial Management (Naira)</h3>
        <p className="text-white/55 text-sm -mt-3 mb-5 max-w-2xl">
          All Financial Management costs are entered and tracked in <span className="text-white/80">Naira (₦)</span>. The NGN → GBP rate used for all FM calculations is the same rate you set in <span className="text-[#D4AF37]">Currency Settings (NGN Override)</span> above — one source of truth for the whole site.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Standard Luxury Box Cost (₦)">
            <Input type="number" value={s.standard_box_cost} onChange={(v) => set("standard_box_cost", Number(v) || 0)} testId="settings-standard-box" />
          </Field>
          <Field label="Premium Luxury Box Cost (₦)">
            <Input type="number" value={s.premium_box_cost} onChange={(v) => set("premium_box_cost", Number(v) || 0)} testId="settings-premium-box" />
          </Field>
          <Field label="International Shipping per kg (₦/kg)">
            <Input type="number" value={s.intl_shipping_per_kg_ngn} onChange={(v) => set("intl_shipping_per_kg_ngn", Number(v) || 0)} testId="settings-intl-shipping-per-kg" />
          </Field>
          <Field label="🇬🇧 UK Shipping Rate (£)">
            <Input type="number" value={s.uk_shipping_rate_gbp} onChange={(v) => set("uk_shipping_rate_gbp", Number(v) || 0)} testId="settings-uk-shipping-rate" />
          </Field>
          <Field label="🇮🇪 Ireland Shipping Rate (€)">
            <Input type="number" value={s.ireland_shipping_rate_eur} onChange={(v) => set("ireland_shipping_rate_eur", Number(v) || 0)} testId="settings-ireland-shipping-rate" />
          </Field>
          <Field label="🌍 Global Shipping Rate (£ base — auto-converts)">
            <Input type="number" value={s.global_shipping_rate_gbp} onChange={(v) => set("global_shipping_rate_gbp", Number(v) || 0)} testId="settings-global-shipping-rate" />
          </Field>
          <Field label="Free Shipping min. spend — Wigs (£)">
            <Input type="number" value={s.free_shipping_min_wigs_gbp} onChange={(v) => set("free_shipping_min_wigs_gbp", Number(v) || 0)} testId="settings-min-spend-wigs" />
          </Field>
          <Field label="Free Shipping min. spend — Hair Tools / Care (£)">
            <Input type="number" value={s.free_shipping_min_other_gbp} onChange={(v) => set("free_shipping_min_other_gbp", Number(v) || 0)} testId="settings-min-spend-other" />
          </Field>
        </div>

        <div className="mt-4 border border-[#D4AF37]/20 bg-[#0F0F0F] p-4 text-xs text-white/60 leading-relaxed">
          <div className="text-[#D4AF37] uppercase tracking-[0.22em] text-[0.65rem] mb-1">How Free Shipping works</div>
          Free Shipping toggles below apply <span className="text-white/85 font-semibold">only</span> when the cart subtotal meets the minimum-spend threshold above.
          Carts containing any <span className="text-white/85">Wig</span> (Luxury Wigs · Braided Wig) use the higher <span className="text-[#D4AF37]">Wigs</span> threshold; mixed carts also use the higher threshold. Below the threshold, the standard flat rate applies.
        </div>

        <div className="mt-6 space-y-3">
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/45">Region-based Free Shipping</div>
          {[
            { key: "free_shipping_ireland", label: "Free Ireland Shipping", flag: "🇮🇪", detail: "Applies to customers whose currency = EUR." },
            { key: "free_shipping_uk",      label: "Free UK Shipping",      flag: "🇬🇧", detail: "Applies to customers whose currency = GBP." },
            { key: "free_shipping_global",  label: "Free Global Shipping",  flag: "🌍", detail: "Overrides every region — free shipping for everyone." },
          ].map((row) => (
            <div key={row.key} className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-5 flex items-start gap-4">
              <label className="flex items-center gap-3 cursor-pointer select-none" data-testid={`settings-${row.key.replace(/_/g,'-')}-toggle`}>
                <span className={`relative inline-block w-11 h-6 transition-colors ${s[row.key] ? "bg-emerald-500" : "bg-[#2A2A2A] border border-[#3A3A3A]"}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(s[row.key])}
                    onChange={(e) => set(row.key, e.target.checked)}
                    className="sr-only"
                  />
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${s[row.key] ? "translate-x-5" : "translate-x-0"}`} />
                </span>
              </label>
              <div className="flex-1">
                <div className="font-serif text-white text-lg">{row.flag} {row.label}</div>
                <div className="text-xs text-white/55 mt-1">
                  {row.detail}
                  {s[row.key] && (
                    <span className="ml-2 text-emerald-400 font-semibold uppercase tracking-[0.18em]">Active</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 border border-[#D4AF37]/30 bg-[#0F0F0F] p-5 flex items-start gap-4">
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="settings-global-sale-toggle">
            <span className={`relative inline-block w-11 h-6 transition-colors ${s.global_sale_override ? "bg-emerald-500" : "bg-[#2A2A2A] border border-[#3A3A3A]"}`}>
              <input
                type="checkbox"
                checked={Boolean(s.global_sale_override)}
                onChange={(e) => set("global_sale_override", e.target.checked)}
                className="sr-only"
              />
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${s.global_sale_override ? "translate-x-5" : "translate-x-0"}`} />
            </span>
          </label>
          <div className="flex-1">
            <div className="font-serif text-white text-lg">Global Sale — Show Sale Prices &amp; Badges</div>
            <div className="text-xs text-white/55 mt-1">
              Controls whether the <span className="text-[#D4AF37]">sale price and sale badge</span> are displayed on the storefront. When <span className="text-white/85">OFF</span> (default) products stay visible at their regular price with no badge — sale info is hidden. When <span className="text-white/85">ON</span> your sale prices &amp; badges are shown. Product visibility is never affected, and DB values are never changed.
              {s.global_sale_override && (
                <span className="ml-2 text-emerald-400 font-semibold uppercase tracking-[0.18em]">Active</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Secure Checkout visibility toggle */}
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Secure Checkout</h3>
        <div className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-5 flex items-start gap-4">
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="settings-secure-checkout-toggle">
            <span className={`relative inline-block w-11 h-6 transition-colors ${s.secure_checkout_enabled === false ? "bg-[#2A2A2A] border border-[#3A3A3A]" : "bg-emerald-500"}`}>
              <input
                type="checkbox"
                checked={s.secure_checkout_enabled !== false}
                onChange={(e) => set("secure_checkout_enabled", e.target.checked)}
                className="sr-only"
              />
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${s.secure_checkout_enabled === false ? "translate-x-0" : "translate-x-5"}`} />
            </span>
          </label>
          <div className="flex-1">
            <div className="font-serif text-white text-lg">Secure Checkout Button</div>
            <div className="text-xs text-white/55 mt-1">
              When ON, customers see the &quot;Secure Checkout&quot; button (Stripe / Klarna). When OFF, only the WhatsApp checkout button is shown. Stripe &amp; Klarna configuration stays intact — flip back ON anytime to instantly restore online payment.
              <span className={`ml-2 font-semibold uppercase tracking-[0.18em] ${s.secure_checkout_enabled === false ? "text-amber-400" : "text-emerald-400"}`}>
                {s.secure_checkout_enabled === false ? "OFF — WhatsApp-only" : "ON — Online payment active"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Confirmation Emails (Resend) */}
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Order Confirmation Emails</h3>
        <div className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-5 flex items-start gap-4 mb-4">
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="settings-order-email-toggle">
            <span className={`relative inline-block w-11 h-6 transition-colors ${s.order_email_enabled ? "bg-emerald-500" : "bg-[#2A2A2A] border border-[#3A3A3A]"}`}>
              <input
                type="checkbox"
                checked={Boolean(s.order_email_enabled)}
                onChange={(e) => set("order_email_enabled", e.target.checked)}
                className="sr-only"
              />
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${s.order_email_enabled ? "translate-x-5" : "translate-x-0"}`} />
            </span>
          </label>
          <div className="flex-1">
            <div className="font-serif text-white text-lg">Automatic Email on Payment Success</div>
            <div className="text-xs text-white/55 mt-1">
              When ON, every customer who completes a Stripe payment receives a branded order confirmation. You are BCC&apos;d on every email.
              {s.order_email_enabled && <span className="ml-2 text-emerald-400 font-semibold uppercase tracking-[0.18em]">Active</span>}
            </div>
          </div>
        </div>
        <Field label="Thank-you note (shown at the top of the email)">
          <Textarea
            value={s.order_email_thank_you_note || ""}
            onChange={(v) => set("order_email_thank_you_note", v)}
            rows={4}
            testId="settings-order-email-thank-you"
          />
        </Field>
        <Field label="Estimated delivery timeframe">
          <Textarea
            value={s.order_email_delivery_timeframe || ""}
            onChange={(v) => set("order_email_delivery_timeframe", v)}
            rows={2}
            testId="settings-order-email-delivery"
          />
        </Field>
      </div>

      {/* Shipment / dispatch emails */}
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Shipment Emails</h3>
        <div className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-5 flex items-start gap-4 mb-4">
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="settings-shipping-email-toggle">
            <span className={`relative inline-block w-11 h-6 transition-colors ${s.shipping_email_enabled ? "bg-emerald-500" : "bg-[#2A2A2A] border border-[#3A3A3A]"}`}>
              <input
                type="checkbox"
                checked={Boolean(s.shipping_email_enabled)}
                onChange={(e) => set("shipping_email_enabled", e.target.checked)}
                className="sr-only"
              />
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${s.shipping_email_enabled ? "translate-x-5" : "translate-x-0"}`} />
            </span>
          </label>
          <div className="flex-1">
            <div className="font-serif text-white text-lg">Automatic Email When Order is Marked Fulfilled</div>
            <div className="text-xs text-white/55 mt-1">
              When you flip an order to <span className="text-[#D4AF37]">Fulfilled</span> in the Orders tab (or click &quot;Mark Fulfilled &amp; Send Email&quot;), the customer receives a branded shipping notification with tracking details. You are BCC&apos;d on every email.
              {s.shipping_email_enabled && <span className="ml-2 text-emerald-400 font-semibold uppercase tracking-[0.18em]">Active</span>}
            </div>
          </div>
        </div>
        <Field label="Shipping thank-you note (shown at the top of the shipment email)">
          <Textarea
            value={s.shipping_email_thank_you_note || ""}
            onChange={(v) => set("shipping_email_thank_you_note", v)}
            rows={4}
            testId="settings-shipping-email-thank-you"
          />
        </Field>
      </div>

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-2">Shop Category Cards</h3>
        <p className="text-white/55 text-sm mb-5 max-w-2xl">
          These images appear on the homepage <span className="text-white/85">Shop by Category</span> strip and on the shop landing grid. If a category has no image, the storefront falls back to the first product photo in that category — so it is never blank.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="admin-category-cards">
          {[
            { slug: "all",              label: "All (shop landing hero)" },
            { slug: "luxury-wigs",      label: "Luxury Wigs" },
            { slug: "braided-wigs",     label: "Braided Wigs" },
            { slug: "hair-extensions",  label: "Hair Extensions" },
            { slug: "hair-care",        label: "Hair Care" },
            { slug: "hair-tools",       label: "Hair Tools" },
            { slug: "essentials",       label: "Beauty Essentials" },
          ].map((row) => {
            const map = s.category_card_images || {};
            return (
              <ImageUploader
                key={row.slug}
                label={row.label}
                value={map[row.slug] || ""}
                onChange={(v) => set("category_card_images", { ...(s.category_card_images || {}), [row.slug]: v })}
                testId={`admin-category-card-${row.slug}`}
              />
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Footer</h3>
        <Field label="Footer Tagline"><Textarea value={s.footer_tagline} onChange={(v) => set("footer_tagline", v)} rows={3} /></Field>
      </div>

      <SaveBar save={save} status={status} />
    </div>
  );
}

function SEOTab() {
  const { s, set, save, status } = useSettingsForm();
  if (!s) return <div className="text-white/40">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-seo-form">
      <Field label="Meta Title (shown in Google search results & browser tab)">
        <Input value={s.meta_title} onChange={(v) => set("meta_title", v)} testId="seo-meta-title" />
      </Field>
      <Field label="Meta Description (shown under the title in search results — keep under 160 characters)">
        <Textarea value={s.meta_description} onChange={(v) => set("meta_description", v)} rows={3} testId="seo-meta-desc" />
      </Field>
      <ImageUploader label="Open Graph / Social Share Image (1200×630 recommended)" value={s.og_image} onChange={(v) => set("og_image", v)} testId="seo-og-image" />
      <SaveBar save={save} status={status} />
    </div>
  );
}

function HeroTab() {
  const { s, set, save, status } = useSettingsForm();
  if (!s) return <div className="text-white/40">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-hero-form">
      <Field label="Eyebrow Tag (small label above the headline)">
        <Input value={s.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} testId="hero-eyebrow-input" />
      </Field>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Hero Headline">
          <Textarea value={s.hero_headline} onChange={(v) => set("hero_headline", v)} rows={3} testId="settings-hero-headline" />
        </Field>
        <Field label="Hero Subheadline">
          <Textarea value={s.hero_subheadline} onChange={(v) => set("hero_subheadline", v)} rows={3} testId="settings-hero-sub" />
        </Field>
      </div>
      <ImageUploader label="Hero Image (replaces the gold-bordered placeholder)" value={s.hero_image} onChange={(v) => set("hero_image", v)} testId="hero-image-upload" />
      <div className="grid md:grid-cols-3 gap-5">
        <Field label="Primary Button Text">
          <Input value={s.hero_cta_browse_text} onChange={(v) => set("hero_cta_browse_text", v)} testId="hero-cta-browse-text" />
        </Field>
        <Field label="Primary Button Link (e.g. #collections)">
          <Input value={s.hero_cta_browse_link} onChange={(v) => set("hero_cta_browse_link", v)} testId="hero-cta-browse-link" />
        </Field>
        <Field label="WhatsApp Button Text">
          <Input value={s.hero_cta_whatsapp_text} onChange={(v) => set("hero_cta_whatsapp_text", v)} testId="hero-cta-whatsapp-text" />
        </Field>
      </div>
      <SaveBar save={save} status={status} />
    </div>
  );
}

function SectionCopyTab() {
  const { s, set, save, status } = useSettingsForm();
  if (!s) return <div className="text-white/40">Loading…</div>;
  return (
    <div className="space-y-8" data-testid="admin-sections-form">
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Collections Section</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Eyebrow"><Input value={s.collections_eyebrow} onChange={(v) => set("collections_eyebrow", v)} /></Field>
          <Field label="Headline"><Input value={s.collections_headline} onChange={(v) => set("collections_headline", v)} /></Field>
          <Field label="Bottom CTA Button Text"><Input value={s.collections_cta_text} onChange={(v) => set("collections_cta_text", v)} /></Field>
        </div>
      </div>
      <div className="gold-hairline" />
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Customer Gallery Section</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Eyebrow"><Input value={s.gallery_eyebrow} onChange={(v) => set("gallery_eyebrow", v)} /></Field>
          <Field label="Headline"><Input value={s.gallery_headline} onChange={(v) => set("gallery_headline", v)} /></Field>
          <Field label="Subheadline"><Textarea value={s.gallery_subheadline} onChange={(v) => set("gallery_subheadline", v)} rows={2} /></Field>
        </div>
      </div>
      <div className="gold-hairline" />
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Why Choose Section</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Eyebrow"><Input value={s.why_eyebrow} onChange={(v) => set("why_eyebrow", v)} /></Field>
          <Field label="Headline"><Input value={s.why_headline} onChange={(v) => set("why_headline", v)} /></Field>
        </div>
      </div>
      <div className="gold-hairline" />
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">How It Works Section</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Eyebrow"><Input value={s.how_eyebrow} onChange={(v) => set("how_eyebrow", v)} /></Field>
          <Field label="Headline"><Input value={s.how_headline} onChange={(v) => set("how_headline", v)} /></Field>
        </div>
      </div>
      <div className="gold-hairline" />
      <div>
        <h3 className="font-serif text-xl text-[#D4AF37] mb-5">Final CTA Section</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Eyebrow"><Input value={s.final_cta_eyebrow} onChange={(v) => set("final_cta_eyebrow", v)} /></Field>
          <Field label="Headline"><Input value={s.final_cta_headline} onChange={(v) => set("final_cta_headline", v)} /></Field>
          <Field label="Body Text"><Textarea value={s.final_cta_text} onChange={(v) => set("final_cta_text", v)} rows={3} /></Field>
          <Field label="Button Text"><Input value={s.final_cta_button_text} onChange={(v) => set("final_cta_button_text", v)} /></Field>
        </div>
      </div>
      <SaveBar save={save} status={status} />
    </div>
  );
}

const WIG_OPTIONS = {
  wig_collection: ["Signature Collection", "Heritage Collection"],
  construction: ["Factory Made", "Custom Made"],
  hair_type: ["Virgin Hair", "Raw Donor Hair"],
  hair_origin: ["Vietnamese", "Cambodian", "Burmese", "Brazilian", "Indian", "Peruvian", "Malaysian", "Chinese", "Other"],
  texture: ["Bone Straight", "Straight", "Body Wave", "Deep Wave", "Water Wave", "Water Curly", "Loose Wave", "Jerry Curly", "Curly", "Bouncy", "Pixie Curls", "Burmese Curls", "Kinky Straight", "Kinky Curly"],
  length: ["8 Inches", "10 Inches", "12 Inches", "14 Inches", "16 Inches", "18 Inches", "20 Inches", "22 Inches", "24 Inches", "26 Inches", "28 Inches", "30 Inches", "32 Inches", "34 Inches", "36 Inches", "38 Inches", "40 Inches"],
  lace_type: ["HD Lace", "Swiss Transparent Lace"],
  lace_size: ["2x4 Closure", "2x6 Closure", "4x4 Closure", "5x5 Closure", "6x6 Closure", "7x7 Closure", "8x8 Closure", "9x9 Closure", "13x4 Frontal", "13x6 Frontal", "360 Lace"],
  density: ["200g", "250g", "300g", "350g", "400g"],
};
function FinancialSection({ item, update, settings }) {
  const [open, setOpen] = useState(false);
  const n = (v) => Number(v) || 0;
  const parseGbp = (s) => {
    const m = String(s || "").match(/(-?\d[\d,]*(\.\d+)?)/);
    return m ? parseFloat(m[1].replace(/,/g, "")) : 0;
  };

  const suggestedPackagingCost =
    item.packaging_type === "Standard Luxury Box" ? n(settings?.standard_box_cost) :
    item.packaging_type === "Premium Luxury Box" ? n(settings?.premium_box_cost) : 0;
  const packagingCost = n(item.packaging_cost);

  // Suggested international shipping: weight (grams) ÷ 1000 × per-kg NGN rate.
  const autoIntlShipping = (n(item.weight_kg) / 1000) * n(settings?.intl_shipping_per_kg_ngn);
  const intlShipping = n(item.intl_shipping_cost);
  const additionalShipping = n(item.additional_shipping_cost);

  const totalCostNgn =
    n(item.purchase_cost) + intlShipping + n(item.customs_cost) +
    n(item.local_delivery_cost) + n(item.styling_cost) + packagingCost +
    n(item.gift_cost) + n(item.other_costs) + additionalShipping;

  const rawMargin = item.target_profit_margin;
  const margin =
    rawMargin === null || rawMargin === undefined || rawMargin === "" || isNaN(Number(rawMargin))
      ? 30
      : Math.max(0, Math.min(100, Number(rawMargin)));
  const suggestedPriceNgn = totalCostNgn * (1 + margin / 100);

  // Single NGN→GBP rate (manual override under Settings → Currency)
  const fmRate = n(settings?.ngn_rate_override); // ₦ per £1
  const toGbp = (ngn) => (fmRate > 0 ? ngn / fmRate : 0);

  const regularPriceGbp = parseGbp(item.price_gbp);
  const regularPriceNgn = regularPriceGbp * fmRate;
  const grossProfitNgn = regularPriceNgn - totalCostNgn;
  const grossMarginPct = regularPriceNgn > 0 ? (grossProfitNgn / regularPriceNgn) * 100 : 0;

  // Sale-price profit calculations (admin-only — sale data is already excluded from public response, and the visual sale badge requires is_on_sale=true)
  const salePriceGbp = parseGbp(item.sale_price_gbp);
  const hasSalePrice = salePriceGbp > 0;
  const salePriceNgn = salePriceGbp * fmRate;
  const saleGrossProfitNgn = salePriceNgn - totalCostNgn;
  const saleGrossMarginPct = salePriceNgn > 0 ? (saleGrossProfitNgn / salePriceNgn) * 100 : 0;

  const stockOptions = [
    { value: "In Stock", label: "In Stock" },
    { value: "Low Stock", label: "Low Stock" },
    { value: "Out of Stock", label: "Out of Stock" },
    { value: "Hidden", label: "Hidden" },
  ];

  const fmtNgn = (v) => `₦${(Number(v) || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;
  const fmtGbp = (v) => `£${(Number(v) || 0).toFixed(2)}`;
  const profitColor = grossProfitNgn >= 0 ? "text-emerald-400" : "text-red-400";

  return (
    <div className="mt-5 border border-emerald-500/25 bg-[#0F1A12]" data-testid="financial-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="financial-toggle"
      >
        <span className="text-[0.7rem] uppercase tracking-[0.28em] text-emerald-400 font-semibold">
          Financial Management {open ? "—" : "+"}
        </span>
        <span className="flex items-center gap-3 text-[0.65rem] uppercase tracking-[0.18em]">
          <span className="text-white/55">Cost {fmtNgn(totalCostNgn)}</span>
          <span className={profitColor}>Profit {fmtNgn(grossProfitNgn)} · {grossMarginPct.toFixed(0)}%</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-[#1F2A1F] p-5 space-y-5">
          {fmRate === 0 && (
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-amber-400 bg-amber-500/10 border border-amber-500/30 px-3 py-2">
              ⚠ Set the NGN Override rate in Settings → Currency to enable GBP conversion.
            </div>
          )}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Product Purchase Cost (₦)">
              <Input type="number" value={item.purchase_cost} onChange={(v) => update({ purchase_cost: n(v) })} testId="fm-purchase-cost" />
            </Field>
            <Field label="Product Weight (grams, box + product)">
              <Input type="number" value={item.weight_kg} onChange={(v) => update({ weight_kg: n(v) })} testId="fm-weight-grams" />
            </Field>
            <Field label="International Shipping (₦)">
              <div className="flex gap-2">
                <Input type="number" value={item.intl_shipping_cost} onChange={(v) => update({ intl_shipping_cost: n(v) })} testId="fm-intl-shipping" />
                {autoIntlShipping > 0 && (
                  <button
                    type="button"
                    onClick={() => update({ intl_shipping_cost: autoIntlShipping })}
                    className="shrink-0 px-3 text-[0.65rem] uppercase tracking-[0.18em] border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition"
                    data-testid="fm-intl-shipping-auto-apply"
                    title={`Use auto: ${n(item.weight_kg)}g × ${fmtNgn(settings?.intl_shipping_per_kg_ngn)}/kg`}
                  >
                    Use {fmtNgn(autoIntlShipping)}
                  </button>
                )}
              </div>
            </Field>
            <Field label="Customs / Import (₦)">
              <Input type="number" value={item.customs_cost} onChange={(v) => update({ customs_cost: n(v) })} testId="fm-customs-cost" />
            </Field>
            <Field label="Local Delivery (₦)">
              <Input type="number" value={item.local_delivery_cost} onChange={(v) => update({ local_delivery_cost: n(v) })} testId="fm-local-delivery" />
            </Field>
            <Field label="Styling Cost (₦)">
              <Input type="number" value={item.styling_cost} onChange={(v) => update({ styling_cost: n(v) })} testId="fm-styling-cost" />
            </Field>
            <Field label="Gift Cost (₦)">
              <Input type="number" value={item.gift_cost} onChange={(v) => update({ gift_cost: n(v) })} testId="fm-gift-cost" />
            </Field>
            <Field label="Other Costs (₦)">
              <Input type="number" value={item.other_costs} onChange={(v) => update({ other_costs: n(v) })} testId="fm-other-costs" />
            </Field>
            <Field label="Additional Shipping Cost (₦)">
              <Input type="number" value={item.additional_shipping_cost} onChange={(v) => update({ additional_shipping_cost: n(v) })} testId="fm-additional-shipping" />
            </Field>
            <Field label="Packaging Type">
              <Select
                value={item.packaging_type}
                onChange={(v) => update({ packaging_type: v })}
                options={[
                  { value: "", label: "— None —" },
                  { value: "Standard Luxury Box", label: `Standard Luxury Box (${fmtNgn(settings?.standard_box_cost)})` },
                  { value: "Premium Luxury Box", label: `Premium Luxury Box (${fmtNgn(settings?.premium_box_cost)})` },
                ]}
              />
            </Field>
            <Field label="Packaging Cost (₦)">
              <div className="flex gap-2">
                <Input type="number" value={item.packaging_cost} onChange={(v) => update({ packaging_cost: n(v) })} testId="fm-packaging-cost" />
                {suggestedPackagingCost > 0 && (
                  <button
                    type="button"
                    onClick={() => update({ packaging_cost: suggestedPackagingCost })}
                    className="shrink-0 px-3 text-[0.65rem] uppercase tracking-[0.18em] border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition"
                    data-testid="fm-packaging-cost-auto-apply"
                    title={`Use ${item.packaging_type} default: ${fmtNgn(suggestedPackagingCost)}`}
                  >
                    Use {fmtNgn(suggestedPackagingCost)}
                  </button>
                )}
              </div>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 border-t border-[#1F2A1F] pt-5">
            <Field label="Total Product Cost (₦)">
              <Input value={fmtNgn(totalCostNgn)} onChange={() => {}} testId="fm-total-cost-ngn" />
            </Field>
            <Field label="Total Product Cost (£ equiv)">
              <Input value={fmtGbp(toGbp(totalCostNgn))} onChange={() => {}} testId="fm-total-cost-gbp" />
            </Field>
            <Field label="Target Profit Margin (%) — type any 0–100">
              <Input
                type="number"
                value={item.target_profit_margin}
                min="0"
                max="100"
                step="1"
                onChange={(v) => {
                  if (v === "" || v === null || v === undefined) {
                    update({ target_profit_margin: 0 });
                    return;
                  }
                  let num = Number(v);
                  if (isNaN(num)) num = 0;
                  if (num < 0) num = 0;
                  if (num > 100) num = 100;
                  update({ target_profit_margin: num });
                }}
                testId="fm-target-margin"
              />
            </Field>
            <Field label="Stock Status">
              <Select value={item.stock_status || "In Stock"} onChange={(v) => update({ stock_status: v })} options={stockOptions} />
            </Field>
            <Field label="Suggested Selling Price (₦)">
              <Input value={fmtNgn(suggestedPriceNgn)} onChange={() => {}} testId="fm-suggested-price-ngn" />
            </Field>
            <Field label="Suggested Selling Price (£ equiv)">
              <div className="flex gap-2">
                <Input value={fmtGbp(toGbp(suggestedPriceNgn))} onChange={() => {}} testId="fm-suggested-price-gbp" />
                {fmRate > 0 && suggestedPriceNgn > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const gbp = toGbp(suggestedPriceNgn);
                      const rounded = Math.round(gbp);
                      const current = String(item.price_gbp || "");
                      const prefix = /^from\s+/i.test(current) ? "From " : "";
                      update({ price_gbp: `${prefix}£${rounded}` });
                    }}
                    className="shrink-0 px-3 text-[0.65rem] uppercase tracking-[0.18em] border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition"
                    data-testid="fm-apply-suggested-to-price"
                    title="Update the product's Regular Price (GBP) to match this suggested price"
                  >
                    Apply to price
                  </button>
                )}
              </div>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 border-t border-[#1F2A1F] pt-5">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Regular Price (₦ equiv)</div>
              <div className="font-serif text-xl text-white">{fmtNgn(regularPriceNgn)}</div>
              <div className="text-xs text-white/45 mt-1">{fmtGbp(regularPriceGbp)} from product price</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Gross Profit (Regular)</div>
              <div className={`font-serif text-xl ${profitColor}`} data-testid="fm-gross-profit-regular-ngn">{fmtNgn(grossProfitNgn)}</div>
              <div className={`text-xs ${profitColor} mt-1`}>{fmtGbp(toGbp(grossProfitNgn))}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Gross Margin (Regular)</div>
              <div className={`font-serif text-xl ${profitColor}`} data-testid="fm-gross-margin-regular-pct">{grossMarginPct.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Stock</div>
              <div className="font-serif text-xl text-white">{item.stock_status || "In Stock"}</div>
            </div>
          </div>

          {hasSalePrice && (
            <div className="border-t border-[#1F2A1F] pt-5" data-testid="fm-sale-profit-panel">
              <div className="text-[0.7rem] uppercase tracking-[0.28em] text-[#25D366] font-semibold mb-3">
                Sale Price Breakdown · Admin only
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Sale Price (₦ equiv)</div>
                  <div className="font-serif text-xl text-white">{fmtNgn(salePriceNgn)}</div>
                  <div className="text-xs text-white/45 mt-1">{fmtGbp(salePriceGbp)} from sale price</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Gross Profit (Sale)</div>
                  <div className={`font-serif text-xl ${saleGrossProfitNgn >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="fm-gross-profit-sale-ngn">{fmtNgn(saleGrossProfitNgn)}</div>
                  <div className={`text-xs ${saleGrossProfitNgn >= 0 ? "text-emerald-400" : "text-red-400"} mt-1`}>{fmtGbp(toGbp(saleGrossProfitNgn))}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Gross Margin (Sale)</div>
                  <div className={`font-serif text-xl ${saleGrossProfitNgn >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="fm-gross-margin-sale-pct">{saleGrossMarginPct.toFixed(1)}%</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-1">Sale active on site</div>
                  <div className={`font-serif text-xl ${item.is_on_sale ? "text-emerald-400" : "text-white/40"}`}>
                    {item.is_on_sale ? "YES" : "NO"}
                  </div>
                  <div className="text-xs text-white/45 mt-1">{item.is_on_sale ? "Customers see this price" : "Toggle ON in form above to apply"}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WigDetailsSection({ item, update }) {
  const [open, setOpen] = useState(false);
  if ((item.category || "Luxury Wigs") !== "Luxury Wigs") return null;
  const toSelectOpts = (arr) => [{ value: "", label: "— Select —" }, ...arr.map((v) => ({ value: v, label: v }))];
  return (
    <div className="mt-5 border border-[#D4AF37]/25 bg-[#0F0F0F]" data-testid="wig-details-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="wig-details-toggle"
      >
        <span className="text-[0.7rem] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold">
          Wig Details {open ? "—" : "+"}
        </span>
        <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/45">
          Luxury Wigs only · {open ? "click to collapse" : "click to expand"}
        </span>
      </button>
      {open && (
        <div className="border-t border-[#1F1F1F] p-5 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(WIG_OPTIONS).map(([key, opts]) => (
              <Field key={key} label={key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}>
                <Select value={item[key]} onChange={(v) => update({ [key]: v })} options={toSelectOpts(opts)} />
              </Field>
            ))}
            {item.hair_origin === "Other" && (
              <Field label="Custom Hair Origin">
                <Input value={item.hair_origin_custom} onChange={(v) => update({ hair_origin_custom: v })} />
              </Field>
            )}
          </div>

          <Field label="Care Guide">
            <Textarea value={item.care_guide} onChange={(v) => update({ care_guide: v })} rows={4} />
          </Field>
        </div>
      )}
    </div>
  );
}

const renderWigDetails = (item, update) => <WigDetailsSection item={item} update={update} />;

const parseGbpNumber = (s) => {
  const m = String(s || "").match(/(-?\d[\d,]*(\.\d+)?)/);
  return m ? parseFloat(m[1].replace(/,/g, "")) : 0;
};

function ProductHeaderMetrics({ item, settings }) {
  const n = (v) => Number(v) || 0;
  const pkg = n(item.packaging_cost);
  const intlShipping = n(item.intl_shipping_cost);
  const totalNgn = n(item.purchase_cost) + intlShipping + n(item.customs_cost) + n(item.local_delivery_cost) + n(item.styling_cost) + pkg + n(item.gift_cost) + n(item.other_costs) + n(item.additional_shipping_cost);
  const fmRate = n(settings?.ngn_rate_override);
  const regGbp = parseGbpNumber(item.price_gbp);
  const regNgn = regGbp * fmRate;
  const profitNgn = regNgn - totalNgn;
  const marginPct = regNgn > 0 ? (profitNgn / regNgn) * 100 : 0;
  const stockColor = item.stock_status === "Out of Stock" ? "text-red-400"
                   : item.stock_status === "Low Stock" ? "text-amber-400"
                   : item.stock_status === "Hidden" ? "text-white/40"
                   : "text-emerald-400";
  const fmtNgn = (v) => `₦${(Number(v) || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-[0.7rem] mb-4 pb-4 border-b border-[#1F1F1F]">
      <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Reg Price</div><div className="text-white font-semibold mt-0.5">£{regGbp.toFixed(2)}</div></div>
      <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Total Cost</div><div className="text-white font-semibold mt-0.5">{fmtNgn(totalNgn)}</div></div>
      <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Profit</div><div className={`${profitNgn >= 0 ? "text-emerald-400" : "text-red-400"} font-semibold mt-0.5`}>{fmRate > 0 ? fmtNgn(profitNgn) : "—"}</div></div>
      <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Margin</div><div className={`${profitNgn >= 0 ? "text-emerald-400" : "text-red-400"} font-semibold mt-0.5`}>{fmRate > 0 ? `${marginPct.toFixed(1)}%` : "—"}</div></div>
      <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Stock</div><div className={`${stockColor} font-semibold mt-0.5`}>{item.stock_status || "In Stock"}</div></div>
    </div>
  );
}


function PagesTab() {
  const qc = useQueryClient();
  const { data: pages = [] } = useQuery({
    queryKey: ["admin", "pages"],
    queryFn: () => api.get("/admin/pages").then((r) => r.data),
    staleTime: 30_000,
  });
  const [drafts, setDrafts] = useState({});
  const [status, setStatus] = useState({});

  const draft = (slug, field) => (drafts[slug] && field in drafts[slug])
    ? drafts[slug][field]
    : (pages.find((p) => p.slug === slug) || {})[field] || "";

  const set = (slug, field, value) => {
    setDrafts((d) => ({ ...d, [slug]: { ...(d[slug] || {}), [field]: value } }));
  };

  const save = async (slug) => {
    setStatus((s) => ({ ...s, [slug]: "saving" }));
    try {
      await api.put(`/admin/pages/${slug}`, drafts[slug] || {});
      setDrafts((d) => ({ ...d, [slug]: undefined }));
      setStatus((s) => ({ ...s, [slug]: "saved" }));
      await qc.invalidateQueries({ queryKey: ["admin", "pages"] });
      setTimeout(() => setStatus((s) => ({ ...s, [slug]: "" })), 1500);
    } catch {
      setStatus((s) => ({ ...s, [slug]: "error" }));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pages-tab">
      <div className="text-white/60 text-sm border border-[#2A2A2A] bg-[#0F0F0F] p-4">
        These pages appear at <code className="text-[#D4AF37]">/pages/&lt;slug&gt;</code> on your storefront and are linked from the footer. Edit the title and body below — paragraphs are separated by a blank line.
      </div>
      {pages.map((p) => (
        <div key={p.slug} className="border border-[#2A2A2A] bg-[#0A0A0A] p-5" data-testid={`admin-page-${p.slug}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">{p.slug}</div>
              <a href={`/pages/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-white/45 hover:text-[#D4AF37]">
                View public page ↗
              </a>
            </div>
            <div className="flex items-center gap-3">
              {status[p.slug] === "saved" && <span className="text-emerald-400 text-xs">Saved ✓</span>}
              {status[p.slug] === "error" && <span className="text-red-400 text-xs">Error</span>}
              <button
                type="button"
                onClick={() => save(p.slug)}
                disabled={status[p.slug] === "saving" || !drafts[p.slug]}
                className="px-4 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#F3E5AB] transition disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`admin-page-save-${p.slug}`}
              >
                {status[p.slug] === "saving" ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <label className="block text-[0.6rem] uppercase tracking-[0.22em] text-white/50 mb-1">Title</label>
          <input
            type="text"
            value={draft(p.slug, "title")}
            onChange={(e) => set(p.slug, "title", e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm mb-3"
            data-testid={`admin-page-title-${p.slug}`}
          />
          <label className="block text-[0.6rem] uppercase tracking-[0.22em] text-white/50 mb-1">Body (leave a blank line between paragraphs)</label>
          <textarea
            value={draft(p.slug, "body")}
            onChange={(e) => set(p.slug, "body", e.target.value)}
            rows={12}
            className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm font-mono leading-relaxed"
            data-testid={`admin-page-body-${p.slug}`}
          />
        </div>
      ))}
    </div>
  );
}


function CollectionsTab() {
  const { settings } = useSettings();
  const extra = (item, update) => (
    <>
      <FinancialSection item={item} update={update} settings={settings} />
      <WigDetailsSection item={item} update={update} />
    </>
  );
  const header = (item) => <ProductHeaderMetrics item={item} settings={settings} />;
  return (
    <ListTab
      resource="collections"
      emptyLabel="No collections yet."
      defaultItem={() => ({ name: "New Product", description: "", category: "Luxury Wigs", price_gbp: "From £120", cta_text: "View Collection", gallery_images: [], order: 0, stock_status: "In Stock", target_profit_margin: 30 })}
      extraSections={extra}
      headerExtra={header}
      fields={[
        { key: "name", label: "Name" },
        {
          key: "category", label: "Category", type: "select",
          options: [
            { value: "Luxury Wigs", label: "Luxury Wigs" },
            { value: "Braided Wig", label: "Braided Wigs" },
            { value: "Extensions", label: "Hair Extensions" },
            { value: "Hair Care", label: "Hair Care" },
            { value: "Hair Tools", label: "Hair Tools" },
            { value: "Essentials", label: "Essentials" },
          ],
        },
        { key: "order", label: "Order (lower = first)", type: "number" },
        { key: "price_gbp", label: "Regular Price (GBP)" },
        { key: "is_on_sale", label: "Sale toggle (must be ON for sale to apply)", type: "toggle" },
        { key: "sale_price_gbp", label: "Sale Price (GBP) — only used when toggle is ON" },
        { key: "sale_start", label: "Sale Start (optional)", type: "date" },
        { key: "sale_end", label: "Sale End (optional)", type: "date" },
        { key: "cta_text", label: "CTA Button Text" },
        { key: "cta_message", label: "WhatsApp Prefill (optional)" },
        { key: "description", label: "Short Description (shown on card)", type: "textarea" },
        { key: "image", label: "Cover Photo", type: "image" },
        { key: "gallery_images", label: "Additional Gallery Photos", type: "imageList" },
      ]}
    />
  );
}

function ListTab({ resource, fields, emptyLabel, defaultItem, extraSections, headerExtra }) {
  const qc = useQueryClient();
  const { data: serverItems = [] } = useQuery({
    queryKey: ["admin", resource],
    queryFn: () => api.get(`/admin/${resource}`).then((r) => r.data),
    staleTime: 30_000,
  });
  const [overrides, setOverrides] = useState({});
  const [status, setStatus] = useState("");
  const [pending, setPending] = useState({});
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("All");

  const items = serverItems.map((it) => ({ ...it, ...(overrides[it.id] || {}) }));

  const update = (id, patch) => {
    setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), ...patch } }));
    setPending((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
  };

  const saveOne = async (it) => {
    const patch = pending[it.id];
    if (!patch) return;
    setStatus("saving");
    try {
      await api.put(`/admin/${resource}/${it.id}`, patch);
      setPending((p) => { const c = { ...p }; delete c[it.id]; return c; });
      setOverrides((o) => { const c = { ...o }; delete c[it.id]; return c; });
      setStatus("saved");
      await qc.invalidateQueries({ queryKey: ["admin", resource] });
      setTimeout(() => setStatus(""), 1500);
    } catch {
      setStatus("error");
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${resource}/${id}`);
    setOverrides((o) => { const c = { ...o }; delete c[id]; return c; });
    setPending((p) => { const c = { ...p }; delete c[id]; return c; });
    await qc.invalidateQueries({ queryKey: ["admin", resource] });
  };

  const addNew = async () => {
    setCreating(true);
    try {
      const minOrder = items.reduce((m, it) => Math.min(m, Number(it.order) || 0), 0);
      const newItem = { ...defaultItem(), order: minOrder - 1 };
      await api.post(`/admin/${resource}`, newItem);
      await qc.invalidateQueries({ queryKey: ["admin", resource] });
    } finally {
      setCreating(false);
    }
  };

  const move = async (item, dir) => {
    const sortedList = [...items].sort((x, y) => (Number(x.order) || 0) - (Number(y.order) || 0));
    const idx = sortedList.findIndex((it) => it.id === item.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sortedList.length) return;
    const a = sortedList[idx];
    const b = sortedList[target];
    const aOrder = Number(a.order ?? idx);
    const bOrder = Number(b.order ?? target);
    setStatus("saving");
    try {
      await api.put(`/admin/${resource}/${a.id}`, { order: bOrder });
      await api.put(`/admin/${resource}/${b.id}`, { order: aOrder });
      setStatus("saved");
      await qc.invalidateQueries({ queryKey: ["admin", resource] });
      setTimeout(() => setStatus(""), 1200);
    } catch {
      setStatus("error");
    }
  };

  // Sort by order for display
  const sortedItems = [...items].sort((x, y) => (Number(x.order) || 0) - (Number(y.order) || 0));

  // Client-side search — matches name, description, category, wig details and price.
  // Stock-status filter (Collections only) is applied first, then the search string.
  const q = search.trim().toLowerCase();
  const stockFiltered = (resource === "collections" && stockFilter !== "All")
    ? sortedItems.filter((it) => (it.stock_status || "In Stock") === stockFilter)
    : sortedItems;
  const displayItems = q
    ? stockFiltered.filter((it) => {
        const hay = [
          it.name, it.description, it.category, it.price_gbp, it.wig_collection,
          it.texture, it.hair_type, it.hair_origin, it.hair_origin_custom,
          it.lace_type, it.lace_size, it.length, it.density, it.tagline,
          it.stock_status, it.cta_text,
        ].map((v) => String(v || "").toLowerCase()).join(" | ");
        return hay.includes(q);
      })
    : stockFiltered;

  const anyFilter = q || (resource === "collections" && stockFilter !== "All");

  const noun = resource === "collections" ? "Product" : resource === "faqs" ? "FAQ" : "Item";

  return (
    <div className="space-y-5" data-testid={`admin-${resource}-list`}>
      <div className="sticky top-[7.5rem] z-20 -mx-5 md:-mx-8 px-5 md:px-8 py-4 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1F1F1F] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-serif text-lg text-white">
            {displayItems.length}{anyFilter ? ` of ${sortedItems.length}` : ""} {(anyFilter ? displayItems.length : sortedItems.length) === 1 ? noun : `${noun}s`}
          </div>
          <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/45 mt-0.5">
            {anyFilter ? "Filtered" : "Use ↑ ↓ at the bottom of each item to reorder"}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {resource === "collections" && (
            <>
              <div className="relative">
                <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products…"
                  className="bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none pl-9 pr-8 py-2 text-white text-sm w-56"
                  data-testid="admin-collections-search"
                  aria-label="Search products"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#D4AF37] p-1"
                    data-testid="admin-collections-search-clear"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm cursor-pointer"
                data-testid="admin-collections-stock-filter"
                aria-label="Filter by stock status"
              >
                <option value="All">All stock statuses</option>
                <option value="In Stock">In Stock</option>
                <option value="Low Stock">Low Stock</option>
                <option value="Out of Stock">Out of Stock</option>
                <option value="Hidden">Hidden</option>
              </select>
            </>
          )}
          <Saver status={status} />
          <button
            onClick={addNew}
            disabled={creating}
            className="btn-primary text-sm"
            data-testid={`admin-${resource}-add`}
          >
            <Plus className="w-4 h-4" /> {creating ? "Adding…" : `Add new ${noun.toLowerCase()}`}
          </button>
        </div>
      </div>

      {items.length === 0 && <div className="text-white/40 py-10 text-center">{emptyLabel}</div>}
      {items.length > 0 && displayItems.length === 0 && (
        <div className="text-white/40 py-10 text-center" data-testid="admin-search-no-results">
          {q
            ? <>No {noun.toLowerCase()}s match &ldquo;<span className="text-white/70">{search}</span>&rdquo;{resource === "collections" && stockFilter !== "All" && <> in <span className="text-white/70">{stockFilter}</span></>}.</>
            : <>No {noun.toLowerCase()}s are currently <span className="text-white/70">{stockFilter}</span>.</>
          }
        </div>
      )}

      <div className="space-y-4">
        {displayItems.map((it, idx) => (
          <div key={it.id} className="border border-[#2A2A2A] bg-[#0A0A0A] p-5" data-testid={`admin-${resource}-row-${it.id}`}>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold mb-3">
              #{idx + 1} {it.image ? "" : ""}
            </div>
            {headerExtra && headerExtra(it)}
            <div className="grid md:grid-cols-2 gap-4">
              {fields.map((f) => (
                <Field key={f.key} label={f.label}>
                  {f.type === "textarea" ? (
                    <Textarea value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: v })} rows={3} />
                  ) : f.type === "select" ? (
                    <Select value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: v })} options={f.options} />
                  ) : f.type === "number" ? (
                    <Input type="number" value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: Number(v) || 0 })} />
                  ) : f.type === "image" ? (
                    <ImageUploader value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: v })} label="" />
                  ) : f.type === "video" ? (
                    <VideoUploader value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: v })} label="" helperText="MP4 / MOV, max 30MB." />
                  ) : f.type === "imageList" ? (
                    <ImageListEditor value={it[f.key] || []} onChange={(v) => update(it.id, { [f.key]: v })} />
                  ) : f.type === "date" ? (
                    <Input type="date" value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: v })} />
                  ) : f.type === "taglist" ? (
                    <Textarea
                      value={Array.isArray(it[f.key]) ? it[f.key].join("\n") : (it[f.key] || "")}
                      onChange={(v) => update(it.id, { [f.key]: v.split("\n").map((x) => x.trim()).filter(Boolean) })}
                      rows={4}
                    />
                  ) : f.type === "toggle" ? (
                    <label className="flex items-center gap-3 cursor-pointer select-none" data-testid={`admin-${resource}-${f.key}-toggle-${it.id}`}>
                      <span
                        className={`relative inline-block w-11 h-6 transition-colors ${it[f.key] ? "bg-emerald-500" : "bg-[#2A2A2A] border border-[#3A3A3A]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(it[f.key])}
                          onChange={(e) => update(it.id, { [f.key]: e.target.checked })}
                          className="sr-only"
                        />
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${it[f.key] ? "translate-x-5" : "translate-x-0"}`}
                        />
                      </span>
                      <span className="text-sm text-white/70">{it[f.key] ? "ON SALE" : "Not on sale"}</span>
                      {it[f.key] && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); update(it.id, { is_on_sale: false, sale_price_gbp: "", sale_start: "", sale_end: "" }); }}
                          className="ml-auto px-3 py-1 text-[0.65rem] uppercase tracking-[0.18em] border border-red-500/40 text-red-400 hover:bg-red-500/10 transition"
                          data-testid={`admin-${resource}-clear-sale-${it.id}`}
                        >
                          Clear sale
                        </button>
                      )}
                    </label>
                  ) : (
                    <Input value={it[f.key]} onChange={(v) => update(it.id, { [f.key]: v })} />
                  )}
                </Field>
              ))}
            </div>
            {extraSections && extraSections(it, (patch) => update(it.id, patch))}
            <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-[#1F1F1F]">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => move(it, -1)}
                  disabled={idx === 0}
                  className="w-9 h-9 border border-[#2A2A2A] hover:border-[#D4AF37] text-white/70 hover:text-[#D4AF37] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move up"
                  data-testid={`admin-${resource}-move-up-${it.id}`}
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => move(it, 1)}
                  disabled={idx === sortedItems.length - 1}
                  className="w-9 h-9 border border-[#2A2A2A] hover:border-[#D4AF37] text-white/70 hover:text-[#D4AF37] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Move down"
                  data-testid={`admin-${resource}-move-down-${it.id}`}
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => removeOne(it.id)} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1" data-testid={`admin-${resource}-delete-${it.id}`}>
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
                <button onClick={() => saveOne(it)} className="btn-primary text-sm" disabled={!pending[it.id]} data-testid={`admin-${resource}-save-${it.id}`}>
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TradeFairTab() {
  const { data: initial } = useQuery({
    queryKey: ["admin-tradefair"],
    queryFn: () => api.get("/tradefair").then((r) => r.data),
    staleTime: 0,
  });
  const [overrides, setOverrides] = useState({});
  const [status, setStatus] = useState("");

  const t = initial ? { ...initial, ...overrides } : null;

  if (!t) return <div className="text-white/40">Loading…</div>;

  const set = (k, v) => setOverrides((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setStatus("saving");
    try {
      await api.put("/admin/tradefair", t);
      setStatus("saved");
      setTimeout(() => setStatus(""), 2000);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-tradefair-form">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Eyebrow (small tag above title)"><Input value={t.eyebrow} onChange={(v) => set("eyebrow", v)} testId="tf-eyebrow" /></Field>
        <Field label="Title"><Input value={t.title} onChange={(v) => set("title", v)} testId="tf-title" /></Field>
        <Field label="Date"><Input value={t.date} onChange={(v) => set("date", v)} testId="tf-date" /></Field>
        <Field label="Venue"><Input value={t.venue} onChange={(v) => set("venue", v)} testId="tf-venue" /></Field>
        <Field label="Stand / Location"><Input value={t.stand} onChange={(v) => set("stand", v)} testId="tf-stand" /></Field>
        <Field label="CTA Button Text"><Input value={t.cta_text} onChange={(v) => set("cta_text", v)} testId="tf-cta-text" /></Field>
        <Field label="CTA Link (leave empty to default to WhatsApp)"><Input value={t.cta_link} onChange={(v) => set("cta_link", v)} testId="tf-cta-link" placeholder="https://eventbrite.com/..." /></Field>
      </div>
      <Field label="Description"><Textarea value={t.description} onChange={(v) => set("description", v)} rows={4} testId="tf-desc" /></Field>
      <ImageUploader label="Event Image (optional)" value={t.image} onChange={(v) => set("image", v)} testId="tf-image-upload" />
      <div className="flex items-center gap-4">
        <button onClick={save} className="btn-primary" data-testid="tf-save"><Save className="w-4 h-4" /> Save</button>
        <Saver status={status} />
      </div>
    </div>
  );
}

function OrdersTab() {
  const qc = useQueryClient();
  const { params, dateFilter, setDateFilter, customFrom, setCustomFrom, customTo, setCustomTo } = useAdminFilter();
  const [productSearch, setProductSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders-report", params],
    queryFn: () =>
      api.get("/admin/orders-report", { params }).then((r) => r.data),
    staleTime: 0,
    refetchInterval: 30_000,
  });

  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [autoExpanded, setAutoExpanded] = useState(false);

  const toggleMonth = (key) => {
    const next = new Set(expandedMonths);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpandedMonths(next);
  };

  const updateStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}`, { status });
    qc.invalidateQueries({ queryKey: ["admin-orders-report"] });
  };

  const updateShipment = async (id, payload) => {
    await api.put(`/admin/orders/${id}`, payload);
    qc.invalidateQueries({ queryKey: ["admin-orders-report"] });
  };

  const resendShipmentEmail = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/resend-shipment-email`);
      if (data?.ok) {
        alert("Shipment email resent successfully.");
      } else {
        alert("Could not resend — check Resend keys / customer email on this order.");
      }
    } catch (e) {
      alert("Error resending: " + (e?.message || "unknown"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this order record?")) return;
    await api.delete(`/admin/orders/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-orders-report"] });
  };

  // ---- derived state (hooks must come before any conditional return) ----
  const monthly = data?.monthly || [];

  // Product name search — matches any order item whose `name` contains the
  // query string (case-insensitive). Applied on top of the currently selected
  // date range so the merchant can ask "how many Faraja wigs did I sell last
  // year?". We keep month grouping but replace each month's orders with the
  // filtered subset so the timeline still reads chronologically.
  const productSearchTrimmed = productSearch.trim().toLowerCase();
  const searchActive = productSearchTrimmed.length > 0;
  const filteredMonthly = useMemo(() => {
    if (!searchActive) return monthly;
    return monthly
      .map((m) => {
        const filteredOrders = (m.orders || []).filter((o) =>
          (o.items || []).some((it) =>
            String(it.name || "").toLowerCase().includes(productSearchTrimmed),
          ),
        );
        // Count only the items whose name matches the query — not every item
        // in the filtered order. This keeps "1 ORDER · 6 ITEMS" from being
        // misleading when "argan" matches 1 unit inside a 6-item order.
        const filteredItemCount = filteredOrders.reduce(
          (n, o) => n + (o.items || []).reduce((s, it) => {
            if (String(it.name || "").toLowerCase().includes(productSearchTrimmed)) {
              return s + Number(it.quantity || 1);
            }
            return s;
          }, 0),
          0,
        );
        return { ...m, orders: filteredOrders, order_count: filteredOrders.length, item_count: filteredItemCount };
      })
      .filter((m) => m.orders.length > 0);
  }, [monthly, searchActive, productSearchTrimmed]);

  // Total units sold across the filtered range for the matched product(s).
  // Only counts matching items — a mixed cart of 1 wig + 2 tools reports "1
  // unit" for a wig query, not "3".
  const searchStats = useMemo(() => {
    if (!searchActive) return { orderCount: 0, unitCount: 0 };
    let unitCount = 0;
    let orderCount = 0;
    for (const m of filteredMonthly) {
      for (const o of m.orders) {
        orderCount += 1;
        for (const it of o.items || []) {
          if (String(it.name || "").toLowerCase().includes(productSearchTrimmed)) {
            unitCount += Number(it.quantity || 1);
          }
        }
      }
    }
    return { orderCount, unitCount };
  }, [filteredMonthly, searchActive, productSearchTrimmed]);

  // Auto-expand the newest month once on first load. When a product search is
  // active, auto-expand every matched month so the merchant sees results
  // immediately without clicking each month header. Placed after the derived
  // state above so it can safely reference searchActive/filteredMonthly.
  useEffect(() => {
    if (searchActive) {
      setExpandedMonths(new Set(filteredMonthly.map((m) => m.month_key)));
    } else if (!autoExpanded && data?.monthly?.length) {
      setExpandedMonths(new Set([data.monthly[0].month_key]));
      setAutoExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchActive, filteredMonthly, autoExpanded, data]);

  if (isLoading) return <div className="text-white/40">Loading orders…</div>;

  const statusOptions = ["whatsapp_sent", "paid", "fulfilled", "cancelled"];
  const statusColor = {
    whatsapp_sent: "text-[#D4AF37]",
    paid: "text-emerald-400",
    fulfilled: "text-blue-400",
    cancelled: "text-red-400",
  };

  const fmtNgn = (v) => `₦${(Number(v) || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;
  const rate = Number(data?.ngn_rate) || 0;
  const toGbp = (ngn) => (rate > 0 ? `£${(ngn / rate).toFixed(2)}` : "");

  const filters = [
    { key: "this-month", label: "This Month" },
    { key: "last-month", label: "Last Month" },
    { key: "last-3-months", label: "Last 3 Months" },
    { key: "this-year", label: "This Year" },
    { key: "last-year", label: "Last Year" },
    { key: "lifetime", label: "Lifetime" },
    { key: "custom", label: "Custom" },
  ];

  const range = data?.range || {};

  return (
    <div className="space-y-6" data-testid="admin-orders-list">
      {/* NOTE: The old "Lifetime Summary" block (Revenue / COGS / Profit / Margin /
          Orders / Items) was removed here to eliminate confusion with the Profit
          Dashboard's top 4 cards. When the merchant needs lifetime totals they
          simply flip the Profit Dashboard filter to "Lifetime" — the top cards
          then reflect exactly the same numbers, with no risk of the two boxes
          disagreeing. */}

      {/* Product name search — filters orders in the current date range by
          any item whose product name contains the query. */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
          <input
            type="text"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            placeholder="Search orders by product name…"
            className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none pl-9 pr-8 py-2 text-white text-sm"
            data-testid="orders-product-search"
            aria-label="Search orders by product name"
          />
          {productSearch && (
            <button
              type="button"
              onClick={() => setProductSearch("")}
              aria-label="Clear product search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#D4AF37] p-1"
              data-testid="orders-product-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {searchActive && (
          <div
            className="flex items-center gap-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-2"
            data-testid="orders-product-search-stats"
          >
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/50">Orders matched</div>
              <div className="font-serif text-lg text-white tabular-nums" data-testid="orders-product-search-order-count">{searchStats.orderCount}</div>
            </div>
            <div className="w-px self-stretch bg-[#D4AF37]/20" />
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#D4AF37]">Units of &ldquo;{productSearch.trim()}&rdquo; sold</div>
              <div className="font-serif text-lg text-[#D4AF37] tabular-nums" data-testid="orders-product-search-unit-count">{searchStats.unitCount}</div>
            </div>
          </div>
        )}
      </div>

      {/* Date filter chips */}
      <div className="flex flex-wrap items-center gap-2 pt-2" data-testid="orders-date-filter">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setDateFilter(f.key)}
            className={`px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.2em] border transition ${dateFilter === f.key ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-white/70 hover:border-[#D4AF37]/60"}`}
            data-testid={`orders-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        {dateFilter === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-1.5 text-white text-xs" data-testid="orders-custom-from" />
            <span className="text-white/45 text-xs">to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-1.5 text-white text-xs" data-testid="orders-custom-to" />
          </div>
        )}
      </div>

      {/* Range summary shown when a non-lifetime filter is active */}
      {dateFilter !== "lifetime" && (
        <div className="border border-[#D4AF37]/25 bg-[#0F0F0F] p-4">
          <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
            Selected Range Summary
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <SummaryCard label="Revenue" value={fmtNgn(range.sales_ngn)} sub={toGbp(range.sales_ngn)} testId="orders-range-revenue" />
            <SummaryCard label="COGS" value={fmtNgn(range.cogs_ngn)} sub={toGbp(range.cogs_ngn)} testId="orders-range-cogs" />
            <SummaryCard label="Profit" value={fmtNgn(range.profit_ngn)} sub={toGbp(range.profit_ngn)} accent={(range.profit_ngn || 0) >= 0 ? "text-emerald-400" : "text-red-400"} testId="orders-range-profit" />
            <SummaryCard label="Margin" value={`${(range.margin_pct ?? 0).toFixed(1)}%`} sub="" accent={(range.profit_ngn || 0) >= 0 ? "text-emerald-400" : "text-red-400"} testId="orders-range-margin" />
            <SummaryCard label="Orders" value={range.order_count || 0} sub="" testId="orders-range-count" />
            <SummaryCard label="Items" value={range.item_count || 0} sub="" testId="orders-range-items" />
          </div>
        </div>
      )}

      {/* Monthly sections */}
      {filteredMonthly.length === 0 && (
        <div className="text-center py-16 border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="admin-orders-empty">
          <div className="font-serif text-xl text-white">
            {searchActive ? `No orders in this range contain "${productSearch.trim()}"` : "No orders in this range"}
          </div>
          <p className="text-white/55 text-sm mt-2">
            {searchActive ? "Try a different product name or widen the date filter." : "Try a different date filter or wait for new orders."}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {filteredMonthly.map((m) => {
          const isOpen = expandedMonths.has(m.month_key);
          const mProfitColor = (m.profit_ngn || 0) >= 0 ? "text-emerald-400" : "text-red-400";
          return (
            <div key={m.month_key} className="border border-[#2A2A2A] bg-[#0A0A0A]" data-testid={`admin-month-${m.month_key}`}>
              <button
                type="button"
                onClick={() => toggleMonth(m.month_key)}
                className="w-full text-left px-5 py-4 flex flex-wrap items-center justify-between gap-3 hover:bg-[#0F0F0F]"
                data-testid={`admin-month-toggle-${m.month_key}`}
              >
                <div className="flex-1 min-w-[180px]">
                  <div className="font-serif text-xl text-white">{m.month_label} {isOpen ? "—" : "+"}</div>
                  <div className="text-[0.65rem] uppercase tracking-[0.2em] text-white/45 mt-0.5">
                    {m.order_count} order{m.order_count !== 1 ? "s" : ""} · {m.item_count} item{m.item_count !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="hidden sm:grid grid-cols-4 gap-6 text-right text-[0.7rem]">
                  <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Revenue</div><div className="text-white font-semibold mt-0.5">{fmtNgn(m.sales_ngn)}</div></div>
                  <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">COGS</div><div className="text-white font-semibold mt-0.5">{fmtNgn(m.cogs_ngn)}</div></div>
                  <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Profit</div><div className={`${mProfitColor} font-semibold mt-0.5`}>{fmtNgn(m.profit_ngn)}</div></div>
                  <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Margin</div><div className={`${mProfitColor} font-semibold mt-0.5`}>{m.margin_pct.toFixed(1)}%</div></div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-[#1F1F1F]">
                  <div className="grid sm:hidden grid-cols-2 gap-3 px-5 py-4 border-b border-[#1F1F1F] text-[0.7rem]">
                    <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Revenue</div><div className="text-white font-semibold mt-0.5">{fmtNgn(m.sales_ngn)}</div></div>
                    <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">COGS</div><div className="text-white font-semibold mt-0.5">{fmtNgn(m.cogs_ngn)}</div></div>
                    <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Profit</div><div className={`${mProfitColor} font-semibold mt-0.5`}>{fmtNgn(m.profit_ngn)}</div></div>
                    <div><div className="text-white/45 uppercase tracking-[0.18em] text-[0.6rem]">Margin</div><div className={`${mProfitColor} font-semibold mt-0.5`}>{m.margin_pct.toFixed(1)}%</div></div>
                  </div>
                  <div className="p-4 space-y-3">
                    {(m.orders || []).map((o) => (
                      <OrderRow key={o.id} o={o} expanded={expandedOrder === o.id} onToggle={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)} onStatus={updateStatus} onRemove={remove} onShipment={updateShipment} onResendShipmentEmail={resendShipmentEmail} statusOptions={statusOptions} statusColor={statusColor} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, accent = "text-white", testId }) {
  return (
    <div className="border border-[#1F1F1F] bg-[#0F0F0F] p-3" data-testid={testId}>
      <div className="text-[0.55rem] uppercase tracking-[0.2em] text-white/45 mb-1">{label}</div>
      <div className={`font-serif text-lg md:text-xl ${accent} break-all`}>{value}</div>
      {sub && <div className="text-[0.65rem] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}

function OrderRow({ o, expanded, onToggle, onStatus, onRemove, onShipment, onResendShipmentEmail, statusOptions, statusColor }) {
  const totalQty = (o.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
  const created = new Date(o.created_at);
  const [tracking, setTracking] = useState({
    tracking_number: o.tracking_number || "",
    carrier: o.carrier || "",
    tracking_url: o.tracking_url || "",
    estimated_delivery_date: o.estimated_delivery_date || "",
  });
  const [saving, setSaving] = useState(false);
  const carriers = ["An Post", "Royal Mail", "DPD", "Evri", "DHL", "UPS", "FedEx", "Other"];

  const saveTracking = async () => {
    setSaving(true);
    try {
      await onShipment(o.id, tracking);
    } finally {
      setSaving(false);
    }
  };

  const markFulfilledAndEmail = async () => {
    if (!tracking.tracking_number && tracking.carrier !== "Other") {
      const ok = window.confirm("You haven't entered a tracking number. Send the email anyway?");
      if (!ok) return;
    }
    setSaving(true);
    try {
      // First save tracking info, then flip status → fulfilled (triggers email server-side).
      await onShipment(o.id, { ...tracking });
      await onStatus(o.id, "fulfilled");
    } finally {
      setSaving(false);
    }
  };

  // Guard the raw Status dropdown so a stray click on "fulfilled" does not fire an
  // empty shipment email. If there's no tracking info yet, block and nudge the
  // merchant to use the tracking editor + Mark Fulfilled button above.
  const handleStatusChange = async (newStatus) => {
    if (newStatus === "fulfilled" && o.status !== "fulfilled" && !o.tracking_number && !tracking.tracking_number) {
      const proceed = window.confirm(
        "⚠️ You haven't added tracking info yet.\n\n" +
        "If you continue, the customer will receive a shipment email WITHOUT a tracking number or track button — bad customer experience.\n\n" +
        "Recommended: Cancel, then fill in the Tracking Number + Carrier above, then click \"Mark Fulfilled & Send Email\".\n\n" +
        "Continue anyway (send email without tracking)?"
      );
      if (!proceed) return;
    }
    await onStatus(o.id, newStatus);
  };
  return (
    <div className="border border-[#2A2A2A] bg-[#0A0A0A]" data-testid={`admin-order-${o.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex flex-wrap items-center justify-between gap-3"
      >
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-serif text-lg text-white">{o.customer_name}</div>
            <div
              className="text-[0.65rem] uppercase tracking-[0.22em] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5"
              data-testid={`admin-order-date-${o.id}`}
              title={created.toString()}
            >
              {created.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
          <div className="text-[0.65rem] uppercase tracking-[0.2em] text-white/45 mt-1">
            #{(o.id || "").slice(0, 8).toUpperCase()} · {created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
        <div className="text-sm text-white/70">{totalQty} item{totalQty !== 1 ? "s" : ""}</div>
        <div className="text-[#D4AF37] font-semibold">{o.converted_total || `£${(o.total_gbp || 0).toFixed(2)}`}</div>
        <div className={`text-[0.65rem] uppercase tracking-[0.2em] font-semibold ${statusColor[o.status] || "text-white/55"}`}>
          {o.status?.replace(/_/g, " ")}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#1F1F1F] px-5 py-5 space-y-4 bg-[#0F0F0F]">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {o.customer_phone && <div><span className="text-white/45">Phone:</span> <span className="text-white">{o.customer_phone}</span></div>}
            {o.customer_email && <div><span className="text-white/45">Email:</span> <span className="text-white">{o.customer_email}</span></div>}
            {o.delivery_notes && (
              <div className="sm:col-span-2"><span className="text-white/45">Delivery / Notes:</span> <span className="text-white">{o.delivery_notes}</span></div>
            )}
            <div><span className="text-white/45">Currency at checkout:</span> <span className="text-white">{o.currency}</span></div>
          </div>

          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Items</div>
            <div className="space-y-2">
              {(o.items || []).map((i, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 text-sm border border-[#1F1F1F] px-3 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    {i.image && <img src={i.image} alt="" className="w-10 h-10 object-cover border border-[#1F1F1F]" />}
                    <div className="min-w-0">
                      <div className="text-white truncate">{i.name}</div>
                      {i.category && <div className="text-[0.65rem] uppercase tracking-[0.18em] text-white/45">{i.category}</div>}
                    </div>
                  </div>
                  <div className="text-white/70 whitespace-nowrap text-xs">
                    {i.quantity} × £{(i.unit_amount_gbp || 0).toFixed(2)} = £{((i.unit_amount_gbp || 0) * (i.quantity || 1)).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right mt-3 text-[#D4AF37] font-serif text-xl">
              £{(o.total_gbp || 0).toFixed(2)} GBP
              {o.converted_total && <span className="text-white/55 text-sm ml-2">({o.converted_total})</span>}
            </div>
          </div>

          <div className="border-t border-[#1F1F1F] pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">Shipment & Tracking</div>
              {o.shipment_email_sent && (
                <span className="text-[0.6rem] uppercase tracking-[0.2em] text-emerald-400 flex items-center gap-1">
                  ✓ Shipment email sent{o.shipment_email_sent_at ? ` · ${new Date(o.shipment_email_sent_at).toLocaleString()}` : ""}
                </span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/50 mb-1">Tracking Number</label>
                <input
                  type="text"
                  value={tracking.tracking_number}
                  onChange={(e) => setTracking({ ...tracking, tracking_number: e.target.value })}
                  placeholder="e.g. AB123456789GB"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
                  data-testid={`admin-order-tracking-number-${o.id}`}
                />
              </div>
              <div>
                <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/50 mb-1">Carrier</label>
                <select
                  value={tracking.carrier}
                  onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
                  data-testid={`admin-order-carrier-${o.id}`}
                >
                  <option value="">Select carrier…</option>
                  {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/50 mb-1">
                  Custom Tracking URL (optional)
                  <span className="text-white/35 normal-case tracking-normal ml-2">— we auto-generate one from carrier if left blank</span>
                </label>
                <input
                  type="url"
                  value={tracking.tracking_url}
                  onChange={(e) => setTracking({ ...tracking, tracking_url: e.target.value })}
                  placeholder="https://…"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
                  data-testid={`admin-order-tracking-url-${o.id}`}
                />
              </div>
              <div>
                <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/50 mb-1">Estimated Delivery Date (optional)</label>
                <input
                  type="text"
                  value={tracking.estimated_delivery_date}
                  onChange={(e) => setTracking({ ...tracking, estimated_delivery_date: e.target.value })}
                  placeholder="e.g. 18 Jul 2026 or 2-4 business days"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
                  data-testid={`admin-order-estimated-delivery-${o.id}`}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={saveTracking}
                disabled={saving}
                className="px-4 py-2 bg-[#1F1F1F] hover:bg-[#2A2A2A] border border-[#2A2A2A] text-white text-xs uppercase tracking-[0.16em] transition disabled:opacity-50"
                data-testid={`admin-order-save-tracking-${o.id}`}
              >
                Save Tracking (no email)
              </button>
              {o.status !== "fulfilled" ? (
                <button
                  type="button"
                  onClick={markFulfilledAndEmail}
                  disabled={saving}
                  className="px-4 py-2 bg-[#D4AF37] hover:bg-[#F3E5AB] text-black text-xs uppercase tracking-[0.16em] font-semibold transition disabled:opacity-50"
                  data-testid={`admin-order-fulfil-and-email-${o.id}`}
                >
                  {saving ? "Sending…" : "Mark Fulfilled & Send Email"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onResendShipmentEmail(o.id)}
                  className="px-4 py-2 border border-[#D4AF37]/60 text-[#D4AF37] text-xs uppercase tracking-[0.16em] hover:bg-[#D4AF37]/10 transition"
                  data-testid={`admin-order-resend-shipment-${o.id}`}
                >
                  Resend Shipment Email
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#1F1F1F]">
            <div className="flex items-center gap-2">
              <label className="text-[0.65rem] uppercase tracking-[0.2em] text-white/55">Status</label>
              <select
                value={o.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-1.5 text-white text-xs"
                data-testid={`admin-order-status-${o.id}`}
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => onRemove(o.id)}
              className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
              data-testid={`admin-order-delete-${o.id}`}
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Cost components shown in the breakdown table under the Profit Dashboard summary
// cards.  Keys must match those emitted by the backend's rollup.cost_breakdown.
const COST_BREAKDOWN_LINES = [
  { key: "purchase_cost",           label: "Product Purchase Cost" },
  { key: "intl_shipping_cost",      label: "International Shipping" },
  { key: "customs_cost",            label: "Customs / Import" },
  { key: "local_delivery_cost",     label: "Local Delivery" },
  { key: "styling_cost",            label: "Styling Cost" },
  { key: "gift_cost",               label: "Gift Cost" },
  { key: "other_costs",             label: "Other Costs" },
  { key: "additional_shipping_cost", label: "Additional Shipping Cost" },
  { key: "packaging_cost",          label: "Packaging Cost" },
];

// Operating expense categories — must match backend EXPENSE_CATEGORIES.
const EXPENSE_CATEGORIES = ["Subscriptions", "Transport", "Meals and Entertainment", "Marketing", "Equipment", "Other"];
// Other-income service types — must match backend OTHER_INCOME_SERVICES.
const OTHER_INCOME_SERVICES = ["Revamping", "Rewigging", "Cake Making", "Other"];

function ProfitDashboard() {
  const { dateFilter, setDateFilter, customFrom, setCustomFrom, customTo, setCustomTo, params, filterLabel } = useAdminFilter();
  const [showIncome, setShowIncome] = useState(false);
  const [showExpenses, setShowExpenses] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-orders-report-profit", params],
    queryFn: () => api.get("/admin/orders-report", { params }).then((r) => r.data),
    staleTime: 30_000,
  });

  const fmtNgn = (v) => `₦${(Number(v) || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;

  if (isLoading) {
    return (
      <div className="mb-8 border border-[#1F1F1F] bg-[#0F0F0F] p-6">
        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Profit Dashboard</div>
        <div className="text-white/40 text-sm">Loading financials…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mb-8 border border-red-500/30 bg-red-500/5 p-6 text-red-300 text-sm">
        Couldn&apos;t load financial summary.
      </div>
    );
  }

  // Everything below reads from the SAME response — this is the single source
  // of truth for the whole dashboard. Cards, breakdowns, and per-tab totals
  // all derive from these numbers so figures can never drift across sections.
  const summary = (dateFilter === "lifetime") ? (data?.lifetime || {}) : (data?.range || {});
  const rate = Number(data?.ngn_rate) || 0;
  const eurRate = Number(data?.eur_rate) || 0;
  const orderCogs = summary.cost_breakdown || {};

  // Finance rollups for the same window: expenses & other-income totals + per-
  // category breakdown emitted by finance_rollup(group_key=...) on the backend.
  const otherIncomeSource = (dateFilter === "lifetime")
    ? (data?.other_income_lifetime || {})
    : (data?.other_income_range || {});
  const expensesSource = (dateFilter === "lifetime")
    ? (data?.expenses_lifetime || {})
    : (data?.expenses_range || {});
  const incomeByService = otherIncomeSource.by || {};
  const opexByCategory = expensesSource.by || {};

  const salesNgn = summary.sales_ngn || 0;
  const cogsNgn = summary.cogs_ngn || 0;
  const otherIncomeNgn = otherIncomeSource.total_ngn || 0;
  const opexNgn = expensesSource.total_ngn || 0;

  const totalIncomeNgn = salesNgn + otherIncomeNgn;
  const totalExpensesNgn = cogsNgn + opexNgn;
  const netNgn = totalIncomeNgn - totalExpensesNgn;
  const netColor = netNgn >= 0 ? "text-emerald-400" : "text-red-400";
  // Avg margin = Net Position / Total Income (falls back to 0 when no income).
  const avgMarginPct = totalIncomeNgn > 0 ? (netNgn / totalIncomeNgn) * 100 : 0;

  // Convert a NGN amount into a "£X · €Y" subtitle line — matches Sales/COGS
  // card convention. Falls back gracefully when a rate isn't configured.
  const gbpEurSubtitle = (ngn) => {
    const parts = [];
    if (rate > 0) parts.push(`£${(ngn / rate).toFixed(2)}`);
    if (rate > 0 && eurRate > 0) parts.push(`€${((ngn / rate) * eurRate).toFixed(2)}`);
    return parts.join(" · ");
  };

  return (
    <div className="mb-8" data-testid="profit-dashboard">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37]">Profit Dashboard</div>
          <div className="font-serif text-2xl text-white mt-1" data-testid="profit-dashboard-filter-label">{filterLabel}</div>
        </div>
        <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/45">
          {summary.order_count || 0} orders · {summary.item_count || 0} items
          {rate > 0 ? ` · NGN→GBP @ ${rate}` : " · ⚠ no NGN rate set"}
        </div>
      </div>

      {/* Global date-filter chips — ONE control that drives every panel on
          the admin dashboard (top cards, breakdowns, Expense Tracker, Other
          Income Tracker, Orders tab). Changing this here filters the entire
          dashboard consistently — no per-tab filter drift. */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="profit-dashboard-date-filter">
        {PROFIT_DATE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setDateFilter(f.key)}
            className={`px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.2em] border transition ${dateFilter === f.key ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-white/70 hover:border-[#D4AF37]/60"}`}
            data-testid={`profit-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        {dateFilter === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-1.5 text-white text-xs" data-testid="profit-custom-from" />
            <span className="text-white/45 text-xs">to</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-1.5 text-white text-xs" data-testid="profit-custom-to" />
          </div>
        )}
      </div>

      {/* Top summary cards — ONE clean set of numbers. Total Income, Total
          Expenses, Net Position (with NGN/GBP/EUR sub-lines), and Avg Margin.
          No duplicates, no conflicting figures anywhere else on the page. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card
          label="Total Income"
          value={fmtNgn(totalIncomeNgn)}
          subtitle={gbpEurSubtitle(totalIncomeNgn)}
          accent="text-white"
          testId="dashboard-total-income"
        />
        <Card
          label="Total Expenses"
          value={fmtNgn(totalExpensesNgn)}
          subtitle={gbpEurSubtitle(totalExpensesNgn)}
          accent="text-white"
          testId="dashboard-total-expenses"
        />
        <Card
          label="Net Position"
          value={fmtNgn(netNgn)}
          subtitle={gbpEurSubtitle(netNgn)}
          accent={netColor}
          testId="dashboard-net-position"
        />
        <Card
          label="Avg Margin"
          value={`${avgMarginPct.toFixed(1)}%`}
          subtitle=""
          accent={netColor}
          testId="dashboard-avg-margin"
        />
      </div>

      {/* Income Breakdown — collapsed by default, expands to show Store Sales
          + Other Service Income (broken down by service_type). */}
      <CollapsibleBreakdown
        testIdPrefix="profit-income-breakdown"
        title="Income Breakdown"
        subtitle={`Store sales + other service income — ${filterLabel}`}
        totalLabel="Total Income"
        totalValue={fmtNgn(totalIncomeNgn)}
        show={showIncome}
        onToggle={() => setShowIncome((v) => !v)}
      >
        <BreakdownLine
          testId="profit-income-line-store-sales"
          label="Store Sales"
          valueNgn={salesNgn}
          totalNgn={totalIncomeNgn}
          rate={rate}
          eurRate={eurRate}
        />
        {OTHER_INCOME_SERVICES.map((svc) => {
          const rec = incomeByService[svc] || {};
          const v = rec.total_ngn || 0;
          return (
            <BreakdownLine
              key={svc}
              testId={`profit-income-line-${svc.toLowerCase().replace(/\s+/g, "-")}`}
              label={svc}
              valueNgn={v}
              totalNgn={totalIncomeNgn}
              rate={rate}
              eurRate={eurRate}
            />
          );
        })}
      </CollapsibleBreakdown>

      {/* Expenses Breakdown — collapsed by default, expands to show the 9
          product COGS components + 6 operating expense categories. */}
      <CollapsibleBreakdown
        testIdPrefix="profit-expenses-breakdown"
        title="Expenses Breakdown"
        subtitle={`Cost of goods + operating expenses — ${filterLabel}`}
        totalLabel="Total Expenses"
        totalValue={fmtNgn(totalExpensesNgn)}
        show={showExpenses}
        onToggle={() => setShowExpenses((v) => !v)}
      >
        {/* Product COGS — 9 lines from orders_report cost_breakdown */}
        <div className="px-5 py-2 text-[0.6rem] uppercase tracking-[0.22em] text-white/40 bg-[#0B0B0B]" data-testid="profit-expenses-heading-cogs">
          Product cost of goods
        </div>
        {COST_BREAKDOWN_LINES.map((line) => (
          <BreakdownLine
            key={line.key}
            testId={`profit-expenses-line-${line.key}`}
            label={line.label}
            valueNgn={Number(orderCogs[line.key] || 0)}
            totalNgn={totalExpensesNgn}
            rate={rate}
            eurRate={eurRate}
          />
        ))}
        {/* Operating expenses — 6 categories from finance_rollup(group_key=category) */}
        <div className="px-5 py-2 text-[0.6rem] uppercase tracking-[0.22em] text-white/40 bg-[#0B0B0B]" data-testid="profit-expenses-heading-opex">
          Operating expenses
        </div>
        {EXPENSE_CATEGORIES.map((cat) => {
          const rec = opexByCategory[cat] || {};
          const v = rec.total_ngn || 0;
          const displayLabel = cat === "Other" ? "Other Operating Expenses" : cat;
          return (
            <BreakdownLine
              key={cat}
              testId={`profit-expenses-line-opex-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              label={displayLabel}
              valueNgn={v}
              totalNgn={totalExpensesNgn}
              rate={rate}
              eurRate={eurRate}
            />
          );
        })}
      </CollapsibleBreakdown>
    </div>
  );
}

function CollapsibleBreakdown({ testIdPrefix, title, subtitle, totalLabel, totalValue, show, onToggle, children }) {
  return (
    <div className="mt-4 border border-[#1F1F1F] bg-[#0F0F0F]" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={show}
        aria-controls={`${testIdPrefix}-body`}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 flex-wrap text-left hover:bg-[#141414] transition-colors"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronDown
            className={`w-4 h-4 text-[#D4AF37] shrink-0 transition-transform duration-300 ${show ? "rotate-180" : ""}`}
            aria-hidden
          />
          <div className="min-w-0">
            <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37]">
              {show ? "Hide" : "View"} {title}
            </div>
            <div className="text-xs text-white/45 mt-1 truncate">{subtitle}</div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/40">{totalLabel}</div>
          <div className="font-serif text-lg text-white" data-testid={`${testIdPrefix}-total`}>{totalValue}</div>
        </div>
      </button>
      {show && (
        <div id={`${testIdPrefix}-body`} className="divide-y divide-[#1F1F1F] border-t border-[#1F1F1F]" data-testid={`${testIdPrefix}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}

function BreakdownLine({ testId, label, valueNgn, totalNgn, rate, eurRate }) {
  const share = totalNgn > 0 ? (valueNgn / totalNgn) * 100 : 0;
  const gbp = rate > 0 ? valueNgn / rate : 0;
  const eur = rate > 0 && eurRate > 0 ? gbp * eurRate : 0;
  return (
    <div className="px-5 py-3 flex items-center justify-between gap-4" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="text-white text-sm">{label}</div>
        <div className="text-[0.6rem] uppercase tracking-[0.2em] text-white/40 mt-0.5">
          {share.toFixed(1)}% of total
          {rate > 0 && ` · £${gbp.toFixed(2)}`}
          {rate > 0 && eurRate > 0 && ` · €${eur.toFixed(2)}`}
        </div>
      </div>
      <div className="font-serif text-base text-white shrink-0 tabular-nums">
        ₦{(Number(valueNgn) || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}


function Card({ label, value, subtitle, accent = "text-white", testId }) {
  return (
    <div className="border border-[#1F1F1F] bg-[#0F0F0F] p-5" data-testid={testId}>
      <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/50 mb-2">{label}</div>
      <div className={`font-serif text-2xl md:text-3xl ${accent}`}>{value}</div>
      {subtitle && <div className="text-xs text-white/40 mt-1.5">{subtitle}</div>}
    </div>
  );
}


function WebhookHealthBanner() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "webhook-health"],
    queryFn: () => api.get("/admin/webhook-health").then((r) => r.data),
    refetchInterval: 60_000, // refresh every minute so a broken webhook surfaces fast
    staleTime: 30_000,
  });
  const [dismissed, setDismissed] = useState(false);

  if (isLoading || !data || dismissed) return null;

  // Only surface loud banners when action is actually needed.
  if (data.status === "healthy" || data.status === "idle") {
    return (
      <div
        className="mb-6 flex items-center gap-3 border border-emerald-500/25 bg-emerald-500/5 px-4 py-2.5"
        data-testid="webhook-health-banner-healthy"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" aria-hidden />
        <span className="text-xs uppercase tracking-[0.22em] text-emerald-300/90">Payment webhook</span>
        <span className="text-xs text-white/60 truncate">{data.message}</span>
        {data.last_webhook_at && (
          <span className="text-[0.65rem] text-white/40 ml-auto pl-3">
            Last event: {new Date(data.last_webhook_at).toLocaleString()}
          </span>
        )}
      </div>
    );
  }

  const color = data.status === "broken"
    ? { border: "border-red-500/60", bg: "bg-red-500/10", text: "text-red-300", label: "URGENT — Payments Failing" }
    : { border: "border-amber-500/60", bg: "bg-amber-500/10", text: "text-amber-300", label: "Warning — Webhook Degraded" };

  return (
    <div
      className={`mb-6 border ${color.border} ${color.bg} p-5`}
      data-testid={`webhook-health-banner-${data.status}`}
    >
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center border ${color.border} ${color.text} font-bold text-lg`}>!</div>
        <div className="flex-1 min-w-0">
          <div className={`text-[0.7rem] uppercase tracking-[0.28em] ${color.text} font-semibold mb-1`}>{color.label}</div>
          <div className="text-white text-sm leading-relaxed">{data.message}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
            <div className="border border-white/10 bg-black/25 p-2.5">
              <div className="text-white/45 uppercase tracking-[0.16em] text-[0.6rem] mb-1">Checkouts (7d)</div>
              <div className="text-white text-base font-semibold">{data.checkouts_last_7d}</div>
            </div>
            <div className="border border-white/10 bg-black/25 p-2.5">
              <div className="text-white/45 uppercase tracking-[0.16em] text-[0.6rem] mb-1">Confirmed Paid</div>
              <div className="text-white text-base font-semibold">{data.paid_last_7d}</div>
            </div>
            <div className="border border-white/10 bg-black/25 p-2.5">
              <div className="text-white/45 uppercase tracking-[0.16em] text-[0.6rem] mb-1">Orders Created</div>
              <div className="text-white text-base font-semibold">{data.orders_last_7d}</div>
            </div>
            <div className="border border-white/10 bg-black/25 p-2.5">
              <div className="text-white/45 uppercase tracking-[0.16em] text-[0.6rem] mb-1">Webhook Events</div>
              <div className="text-white text-base font-semibold">{data.webhooks_received_last_7d}</div>
            </div>
          </div>
          {data.status === "broken" && (
            <div className="mt-4 text-xs text-white/70 leading-relaxed">
              <div className="font-semibold text-white mb-1">How to fix:</div>
              <ol className="list-decimal pl-4 space-y-1">
                <li>Open Stripe Dashboard → Developers → Webhooks → copy your endpoint&apos;s signing secret</li>
                <li>In Emergent → your deployment → Environment Variables → update <code className="bg-black/40 px-1">STRIPE_WEBHOOK_SECRET</code></li>
                <li>Click <strong>Republish</strong>. Verify by resending a webhook from the Stripe dashboard — it should return 200.</li>
              </ol>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss banner"
          className="text-white/50 hover:text-white text-lg leading-none flex-shrink-0"
          data-testid="webhook-health-banner-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const { admin, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    if (ready && !admin) nav("/admin/login", { replace: true });
  }, [ready, admin, nav]);

  if (!ready || !admin) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <AdminFilterProvider>
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-[#1F1F1F] bg-[#0A0A0A] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-xs text-white/55 hover:text-[#D4AF37] flex items-center gap-1" data-testid="admin-view-site">
              View site <ExternalLink className="w-3 h-3" />
            </a>
            <button onClick={() => { logout(); nav("/admin/login"); }} className="text-white/70 hover:text-[#D4AF37] flex items-center gap-1 text-sm" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.18em] border-b-2 whitespace-nowrap transition-colors ${
                tab === t.id ? "text-[#D4AF37] border-[#D4AF37]" : "text-white/60 border-transparent hover:text-white"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <WebhookHealthBanner />
        <ProfitDashboard />
        {tab === "settings" && <SettingsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "expenses" && (
          <FinanceTrackerPanel
            resource="expenses"
            eyebrow="Expense Tracker"
            headline="Business Operating Expenses"
            helperText="Log day-to-day running costs of the business — subscriptions, transport, marketing, etc. These are strictly separate from per-product costs already captured on each Collection item (which flow into COGS on the Profit Dashboard). Never visible on the storefront."
            categoryField={{
              key: "category",
              label: "Category",
              options: ["Subscriptions", "Transport", "Meals and Entertainment", "Marketing", "Equipment", "Other"],
            }}
            descriptionField={{ key: "description", label: "Description", placeholder: "What was it for?" }}
          />
        )}
        {tab === "other_income" && (
          <FinanceTrackerPanel
            resource="other_income"
            eyebrow="Other Income Tracker"
            headline="Income Outside The Store"
            helperText="Track earnings from services outside the wig store — revamping, rewigging, cake making, etc. These add to your Total Income in the Profit Dashboard's Monthly Summary. Never visible on the storefront."
            categoryField={{
              key: "service_type",
              label: "Service",
              options: ["Revamping", "Rewigging", "Cake Making", "Other"],
            }}
            descriptionField={{ key: "client_note", label: "Client note", placeholder: "Client / notes" }}
          />
        )}
        {tab === "seo" && <SEOTab />}
        {tab === "hero" && <HeroTab />}
        {tab === "sections" && <SectionCopyTab />}
        {tab === "collections" && (
          <CollectionsTab />
        )}
        {tab === "pages" && (
          <PagesTab />
        )}
        {tab === "gallery" && (
          <ListTab
            resource="gallery"
            emptyLabel="No gallery items yet."
            defaultItem={() => ({ label: "Real Customer", caption: "", media_type: "photo", order: 0 })}
            fields={[
              { key: "label", label: "Label / Tag" },
              { key: "caption", label: "Caption" },
              {
                key: "media_type", label: "Type", type: "select",
                options: [
                  { value: "photo", label: "Customer photo" },
                  { value: "video", label: "Customer video" },
                  { value: "before_after", label: "Before & After" },
                ],
              },
              { key: "order", label: "Order (lower = first)", type: "number" },
              { key: "image", label: "Image (used as poster/thumbnail when Type = Video)", type: "image" },
              { key: "video", label: "Video (only used when Type = Video)", type: "video" },
            ]}
          />
        )}
        {tab === "pillars" && (
          <ListTab
            resource="pillars"
            emptyLabel="No trust pillars yet."
            defaultItem={() => ({ icon: "Sparkles", title: "New Reason", description: "", order: 0 })}
            fields={[
              { key: "title", label: "Title" },
              { key: "icon", label: "Lucide Icon Name (e.g. Crown, ShieldCheck, Heart, Truck, Sparkles, Video, Star, Award)" },
              { key: "order", label: "Order", type: "number" },
              { key: "description", label: "Description", type: "textarea" },
            ]}
          />
        )}
        {tab === "steps" && (
          <ListTab
            resource="steps"
            emptyLabel="No steps yet."
            defaultItem={() => ({ number: "01", title: "New Step", description: "", order: 0 })}
            fields={[
              { key: "number", label: "Step Number Label" },
              { key: "title", label: "Step Title" },
              { key: "order", label: "Order", type: "number" },
              { key: "description", label: "Description", type: "textarea" },
            ]}
          />
        )}
        {tab === "faqs" && (
          <ListTab
            resource="faqs"
            emptyLabel="No FAQs yet."
            defaultItem={() => ({ question: "New question", answer: "", order: 0 })}
            fields={[
              { key: "question", label: "Question" },
              { key: "order", label: "Order", type: "number" },
              { key: "answer", label: "Answer", type: "textarea" },
            ]}
          />
        )}
        {tab === "tradefair" && <TradeFairTab />}
      </main>
    </div>
    </AdminFilterProvider>
  );
}
